import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { EditOutlined, ShieldRounded } from "@mui/icons-material";
import {
  GlobalSidePanel,
  PanelHeaderIconButton,
} from "components/DesignSystem/GlobalSidePanel";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { roleAndGroupUi, statusChip } from "../ui";
import {
  RelatedItemsCard,
  SearchField,
  roleKitType,
  teal,
  usedInCompaniesLabel,
} from "../UserRole/roleUiKit";
import { matchesUserSearch } from "./userSearch";
import { normalizeGroupMembers } from "./userGroupPayload";
import {
  CompanyIcon,
  GroupIcon,
  MEMBER_PREVIEW,
  MoreRow,
  UserAvatar,
  departmentAccessLabel,
  getGroupStatus,
  getRolesForGroup,
  groupAssignmentsByCompany,
  groupTypography,
  roleNames,
} from "./groupUtils";

const headCellSx = {
  fontSize: 13,
  fontWeight: 700,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  backgroundColor: designColors.slateBg,
  borderBottom: `1px solid ${designColors.slate200}`,
  py: 1.25,
  px: 2,
};

const bodyCellSx = {
  fontSize: 14,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate100}`,
  py: 1.25,
  px: 2,
};

const tableBoxSx = {
  border: `1px solid ${designColors.slate200}`,
  borderRadius: "10px",
  overflowX: "auto",
};

const cardSx = { ...roleAndGroupUi.sectionCard, p: { xs: 2, md: 2.25 } };

// Company column: hidden on phones (shown under the name there instead).
const viewCompanyColumnSx = { width: { sm: "26%" }, display: { xs: "none", sm: "table-cell" } };

// hidden: temporarily hide the panel (Edit open over it) without closing
// it, so its loaded details and state are kept and it returns instantly.
// refreshKey: changing it refetches the group (after an edit saves).
export const UserGroupViewPanel = ({
  open,
  group,
  onClose,
  canEdit,
  onEdit,
  // Opens the View Role panel for a role in "Related User Roles"; omitted
  // when the user cannot view Roles (rows then render without a chevron).
  onOpenRole,
  hidden = false,
  refreshKey = 0,
  // The mode Edit was last in; applied when the view is shown again.
  restoreExpanded,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // First MEMBER_PREVIEW users, then a "+N more users" row -- same as the
  // Add/Edit form (2026-10-01, user request; replaced page controls).
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // While hidden behind Edit, follow the mode Edit is in, so the view comes
  // back already in that mode: collapsing Edit to the side panel and closing
  // it lands on the side panel view (2026-10-01, user request). Syncing only
  // once shown used to flash the old full view first.
  useEffect(() => {
    if (hidden && typeof restoreExpanded === "boolean") setExpanded(restoreExpanded);
  }, [hidden, restoreExpanded]);

  useEffect(() => {
    setSearch("");
    setShowAllUsers(false);
    if (!open) setExpanded(false);
    if (!open || !group?.id) {
      setDetails(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    customGetAPI(`${domain}create-assign/user-group/?id=${group.id}`)
      .then((res) => {
        if (!cancelled) setDetails(res?.responseDetails ?? null);
      })
      .catch(() => {
        if (!cancelled) setDetails(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain, group?.id, open, refreshKey]);

  const data = details ?? group ?? {};
  const companies = useMemo(() => groupAssignmentsByCompany(data?.assignments), [data?.assignments]);
  const relatedRoles = useMemo(() => getRolesForGroup(data?.assignments), [data?.assignments]);
  // Scope belongs to the user, so a user with access in several companies
  // is one member, not one row per company. Same helper the edit form uses.
  const users = useMemo(
    () => normalizeGroupMembers(data?.users ?? []).map((user) => ({
      ...user,
      company: user.company ?? companies.find((company) => String(company.company_id) === String(user.company_id))?.company_name,
    })).filter((user) => matchesUserSearch(user, search)),
    [companies, data?.users, search],
  );

  useEffect(() => {
    setShowAllUsers(false);
  }, [search]);

  if (!group || hidden) return null;

  const status = getGroupStatus(data);
  const pagedUsers =
    showAllUsers || users.length <= MEMBER_PREVIEW ? users : users.slice(0, MEMBER_PREVIEW);

  const header = (
    <Mui.Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      <GroupIcon group={group} size={64} />
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <Mui.Typography
            sx={{ fontSize: 22, fontWeight: 700, fontFamily: designFontFamily, color: designColors.slate800 }}
          >
            {data?.name}
          </Mui.Typography>
          {statusChip(status)}
        </Mui.Stack>
        {data?.description ? (
          <Mui.Typography sx={{ mt: 0.5, fontSize: 14, color: designColors.slate600 }}>
            {data.description}
          </Mui.Typography>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
  );

  // Edit icon next to the expand/close icons, in the side panel and the
  // full view alike (bigger in the full view) -- see PanelHeaderIconButton.
  const editButton = !canEdit ? null : (
    <PanelHeaderIconButton
      expanded={expanded}
      title="Edit Group"
      // Tells the parent whether this view is expanded, so Edit opens in
      // the same mode (2026-10-01, user request).
      onClick={() => onEdit(details ?? group, { expanded })}
    >
      <EditOutlined />
    </PanelHeaderIconButton>
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={() => setExpanded((prev) => !prev)}
      header={header}
      headerActions={editButton}
      breadcrumb={["Administration", "User Groups", "View User Group"]}
      bodySx={{ backgroundColor: designColors.white }}
    >
      {loading ? (
        <Loader info="Loading..." minHeight="60vh" />
      ) : (
        <Mui.Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 } }}>
          <Mui.Box sx={cardSx}>
            <Mui.Typography sx={roleKitType.sectionTitle}>Companies & Role Assignment</Mui.Typography>
            <Mui.Typography sx={{ ...roleKitType.sectionSubtitle, mb: 2 }}>
              Assigned roles in each company for this user group.
            </Mui.Typography>
            <Mui.TableContainer sx={tableBoxSx}>
              <Mui.Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell sx={{ ...headCellSx, width: "48%" }}>Company</Mui.TableCell>
                    <Mui.TableCell sx={headCellSx}>Role(s)</Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {companies.length === 0 ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={2} sx={{ ...bodyCellSx, textAlign: "center", color: designColors.slate600 }}>
                        No companies assigned yet.
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    companies.map((company) => (
                      <Mui.TableRow
                        key={company.company_id ?? company.company_name}
                        sx={{ "&:last-child td": { borderBottom: "none" } }}
                      >
                        <Mui.TableCell sx={bodyCellSx}>
                          <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                            <CompanyIcon size={40} tinted />
                            <Mui.Typography component="div" sx={{ ...groupTypography.cellStrong, minWidth: 0, flex: 1 }}>
                              <TruncatedTableValue value={company.company_name} />
                            </Mui.Typography>
                          </Mui.Stack>
                        </Mui.TableCell>
                        <Mui.TableCell sx={{ ...bodyCellSx, color: designColors.slate700, overflowWrap: "anywhere" }}>
                          {roleNames(company) || "-"}
                        </Mui.TableCell>
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Box>

          {/* Mirror of "Related User Groups" on View Role: one row per role
              this group assigns, opening that role's view (2026-10-14). The
              table above keeps the per-company detail. */}
          <RelatedItemsCard
            sx={cardSx}
            icon={<ShieldRounded />}
            iconColor={teal}
            title="Related User Roles"
            subtitle="Roles currently assigned to this user group."
            countLabel={`${relatedRoles.length} role${relatedRoles.length === 1 ? "" : "s"}`}
            emptyText="No roles assigned to this user group yet."
            items={relatedRoles.map((role) => ({
              key: role.role_id ?? role.role_name,
              name: role.role_name || `Role #${role.role_id}`,
              description: role.description,
              meta: usedInCompaniesLabel(role.companyCount),
              data: role,
            }))}
            onOpen={onOpenRole}
            wide={expanded}
          />

          {/* Same bordered card as Companies & Role Assignment above. In full
              view the search sits beside the heading at a fixed width instead
              of stretching across the whole screen. */}
          <Mui.Box sx={cardSx}>
            <Mui.Stack
              direction={expanded ? { xs: "column", md: "row" } : "column"}
              spacing={1.5}
              alignItems={expanded ? { xs: "stretch", md: "center" } : "stretch"}
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Mui.Box sx={{ minWidth: 0 }}>
                <Mui.Typography sx={roleKitType.sectionTitle}>Users in this group</Mui.Typography>
                <Mui.Typography sx={roleKitType.sectionSubtitle}>
                  Users and their department access.
                </Mui.Typography>
              </Mui.Box>
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Search users..."
                sx={{ width: expanded ? { xs: "100%", md: 320 } : "100%", minWidth: 0, flexShrink: 0 }}
              />
            </Mui.Stack>
            <Mui.TableContainer sx={tableBoxSx}>
              {/* No minWidth, so no horizontal scroll: the columns fit the
                  panel, the user code sits under the name, and long values
                  truncate with the styled tooltip on hover. */}
              <Mui.Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell sx={{ ...headCellSx, whiteSpace: "nowrap" }}>Name</Mui.TableCell>
                    <Mui.TableCell sx={{ ...headCellSx, ...viewCompanyColumnSx, whiteSpace: "nowrap" }}>
                      Company
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...headCellSx, width: { xs: 130, sm: "36%" }, whiteSpace: "nowrap" }}>
                      Department Access
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {pagedUsers.length === 0 ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={3} sx={{ ...bodyCellSx, textAlign: "center", color: designColors.slate600 }}>
                        {search.trim() ? "No users match your search." : "No users in this group yet."}
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    pagedUsers.map((user, index) => (
                      <Mui.TableRow
                        key={`${user?.user_code ?? user?.id}-${index}`}
                        sx={{ "&:last-child td": { borderBottom: "none" } }}
                      >
                        <Mui.TableCell sx={bodyCellSx}>
                          <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                            <UserAvatar name={user?.name} image={user?.image_url} />
                            <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                              <Mui.Typography component="div" sx={{ fontSize: 13.5, fontWeight: 600 }}>
                                <TruncatedTableValue value={user?.name || "-"} />
                              </Mui.Typography>
                              <Mui.Typography component="div" sx={{ fontSize: 12, color: designColors.slate600 }}>
                                <TruncatedTableValue
                                  value={[user?.user_code || "-", user?.company].filter(Boolean).join(" · ")}
                                >
                                  {user?.user_code || "-"}
                                  {/* Company column hides on phones; show it here instead. */}
                                  <Mui.Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                                    {user?.company ? ` · ${user.company}` : ""}
                                  </Mui.Box>
                                </TruncatedTableValue>
                              </Mui.Typography>
                            </Mui.Box>
                          </Mui.Stack>
                        </Mui.TableCell>
                        <Mui.TableCell sx={{ ...bodyCellSx, ...viewCompanyColumnSx }}>
                          <TruncatedTableValue value={user?.company || "-"} />
                        </Mui.TableCell>
                        <Mui.TableCell sx={bodyCellSx}>
                          <TruncatedTableValue value={departmentAccessLabel(user)} />
                        </Mui.TableCell>
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {users.length > MEMBER_PREVIEW ? (
              <MoreRow
                expanded={showAllUsers}
                hiddenCount={users.length - MEMBER_PREVIEW}
                noun={users.length - MEMBER_PREVIEW === 1 ? "user" : "users"}
                onToggle={() => setShowAllUsers((prev) => !prev)}
              />
            ) : null}
          </Mui.Box>
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
